import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSupportCompanies } from '@/api/support-api'
import { useUpdateFeatureFlag } from '@/hooks/use-feature-flags'
import { useDebouncedSnapshot } from '@/lib/debounce'
import type {
  FeatureFlagDTO,
  FeatureFlagRule,
} from '@/schemas/feature-flag.schema'

export function useFeatureFlagEditor(flag: FeatureFlagDTO) {
  // Only the two rule fields.
  const [rule, setRule] = useState<FeatureFlagRule>({
    audience: flag.audience,
    whitelist: flag.whitelist,
  })
  const [search, setSearch] = useState('')
  const [names, setNames] = useState<Record<number, string>>({})
  const debouncedSearch = useDebouncedSnapshot(search.trim(), 250)
  // A pasted list of ids (5345, 5341 5328): the API answers those companies,
  // all on one page, and the editor can add them at once.
  const pastedIds = /^#?\d+([\s,;]+#?\d+)+$/.test(debouncedSearch)
    ? debouncedSearch
        .split(/[\s,;]+/)
        .map((token) => Number(token.replace('#', '')))
    : []
  const companies = useQuery({
    queryKey: ['feature-flag-companies', debouncedSearch],
    queryFn: () =>
      getSupportCompanies({
        search: debouncedSearch,
        limit: Math.max(50, pastedIds.length),
      }),
    enabled: Boolean(debouncedSearch),
  })
  const mutation = useUpdateFeatureFlag()
  // Sorted: removing a company and adding it back changes the order, not the rule.
  const sorted = (ids: number[]) =>
    JSON.stringify([...ids].sort((a, b) => a - b))
  const dirty =
    rule.audience !== flag.audience ||
    sorted(rule.whitelist) !== sorted(flag.whitelist)
  function update<K extends keyof FeatureFlagRule>(
    key: K,
    value: FeatureFlagRule[K],
  ) {
    setRule((previous) => ({ ...previous, [key]: value }))
  }
  function addCompanies(added: { id: number; name: string }[]) {
    setNames((previous) => ({
      ...previous,
      ...Object.fromEntries(added.map(({ id, name }) => [id, name])),
    }))
    setRule((previous) => ({
      ...previous,
      whitelist: [
        ...new Set([...previous.whitelist, ...added.map(({ id }) => id)]),
      ],
    }))
  }
  const allNames: Record<number, string> = {
    ...names,
    ...Object.fromEntries(
      (flag.companies ?? []).map((company) => [company.id, company.name]),
    ),
  }
  return {
    rule,
    update,
    addCompany: (id: number, name: string) => addCompanies([{ id, name }]),
    addCompanies,
    pastedIds,
    removeCompany: (id: number) =>
      update(
        'whitelist',
        rule.whitelist.filter((value) => value !== id),
      ),
    search,
    setSearch,
    companies,
    names: allNames,
    searching: search.trim() !== debouncedSearch || companies.isFetching,
    dirty,
    mutation,
    reset: () =>
      setRule({ audience: flag.audience, whitelist: flag.whitelist }),
    save: () =>
      mutation.mutate({
        key: flag.key,
        rule,
        companies: rule.whitelist.flatMap((id) =>
          allNames[id] ? [{ id, name: allNames[id] }] : [],
        ),
      }),
  }
}
